import { useState, useEffect, useRef } from "react";
import { Link, useLocation } from "react-router-dom";

export default function LoggedOutNavbar() {
  const [scrolled, setScrolled] = useState(false);
  const [accountOpen, setAccountOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    if (!accountOpen) return;
    const handleClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (!target.closest(".account-menu")) setAccountOpen(false);
    };
    document.addEventListener("click", handleClick);
    return () => document.removeEventListener("click", handleClick);
  }, [accountOpen]);

  const isActive = (path: string) => location.pathname === path;

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? "bg-white/95 backdrop-blur-md border-b border-foreground-200"
          : "bg-white border-b border-foreground-100"
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 md:px-6 flex items-center justify-between h-16">
        {/* Logo */}
        <Link to="/" className="flex items-center gap-2 flex-shrink-0">
          <img
            src="https://static.readdy.ai/image/4371883fa286ac0088227d507facd13a/cee86cc6ab83fcd7b536d5a6d3288f01.png"
            alt="Man Aya DataLink"
            className="h-9 w-auto object-contain"
          />
          <span className="font-bold text-foreground-950 text-sm sm:hidden">Man Aya DataLink</span>
          <span className="text-foreground-900 font-bold text-base whitespace-nowrap hidden sm:inline">Man Aya DataLink</span>
        </Link>

        {/* Center nav links */}
        <div className="hidden lg:flex items-center gap-1">
          <Link
            to="/buy"
            className={`px-4 py-2 rounded-lg text-sm font-semibold cursor-pointer transition-colors whitespace-nowrap ${
              isActive("/buy")
                ? "text-primary-500 bg-primary-50"
                : "text-foreground-700 hover:text-foreground-950 hover:bg-foreground-100"
            }`}
          >
            Buy Data
          </Link>
          <Link
            to="/reseller"
            className={`px-4 py-2 rounded-lg text-sm font-semibold cursor-pointer transition-colors whitespace-nowrap ${
              isActive("/reseller")
                ? "text-secondary-600 bg-secondary-50"
                : "text-foreground-700 hover:text-foreground-950 hover:bg-foreground-100"
            }`}
          >
            Become a Reseller
          </Link>
          <Link
            to="/monthly-challenge"
            className={`px-4 py-2 rounded-lg text-sm font-semibold cursor-pointer transition-colors whitespace-nowrap ${
              isActive("/monthly-challenge")
                ? "text-accent-600 bg-accent-50"
                : "text-foreground-700 hover:text-foreground-950 hover:bg-foreground-100"
            }`}
          >
            What&apos;s New
          </Link>
          <Link
            to="/track-order"
            className={`px-4 py-2 rounded-lg text-sm font-semibold cursor-pointer transition-colors whitespace-nowrap ${
              isActive("/track-order")
                ? "text-primary-500 bg-primary-50"
                : "text-foreground-700 hover:text-foreground-950 hover:bg-foreground-100"
            }`}
          >
            Track Order
          </Link>
        </div>

        {/* Right side: Account dropdown + mobile menu */}
        <div className="flex items-center gap-2">
          {/* Account dropdown */}
          <div className="relative account-menu hidden md:block">
            <button
              onClick={(e) => {
                e.stopPropagation();
                setAccountOpen(!accountOpen);
              }}
              className="flex items-center gap-2 text-sm font-semibold text-foreground-700 hover:text-foreground-950 px-4 py-2 rounded-lg hover:bg-foreground-100 cursor-pointer transition-colors whitespace-nowrap"
            >
              <i className="ri-user-3-line text-base w-5 h-5 flex items-center justify-center"></i>
              Account
              <i className={`ri-arrow-down-s-line text-xs transition-transform ${accountOpen ? "rotate-180" : ""}`}></i>
            </button>
            {accountOpen && (
              <div className="absolute right-0 top-full mt-1 w-44 bg-white border border-foreground-200 rounded-xl shadow-lg py-1 z-50">
                <Link
                  to="/login"
                  onClick={() => setAccountOpen(false)}
                  className="flex items-center gap-2 px-4 py-2.5 text-sm font-medium text-foreground-700 hover:bg-foreground-50 cursor-pointer transition-colors"
                >
                  <i className="ri-login-box-line text-base w-5 h-5 flex items-center justify-center text-foreground-400"></i>
                  Sign In
                </Link>
                <Link
                  to="/register"
                  onClick={() => setAccountOpen(false)}
                  className="flex items-center gap-2 px-4 py-2.5 text-sm font-medium text-foreground-700 hover:bg-foreground-50 cursor-pointer transition-colors"
                >
                  <i className="ri-user-add-line text-base w-5 h-5 flex items-center justify-center text-foreground-400"></i>
                  Create Account
                </Link>
              </div>
            )}
          </div>

          {/* Mobile: hamburger toggle */}
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="lg:hidden flex items-center justify-center w-9 h-9 rounded-lg hover:bg-foreground-100 cursor-pointer"
            aria-label="Toggle menu"
          >
            <i className={`text-xl ${mobileMenuOpen ? "ri-close-line" : "ri-menu-line"}`}></i>
          </button>

          {/* CTA */}
          <Link
            to="/register"
            className="bg-primary-500 hover:bg-primary-600 text-white text-sm font-semibold px-5 py-2.5 rounded-full whitespace-nowrap cursor-pointer transition-colors hidden sm:inline-flex"
          >
            Sign Up
          </Link>
        </div>
      </div>

      {/* Mobile toggle menu */}
      {mobileMenuOpen && (
        <div className="lg:hidden border-t border-foreground-100 py-2 px-4 space-y-1">
          <Link to="/buy" onClick={() => setMobileMenuOpen(false)} className={`block py-2 text-sm font-medium ${isActive("/buy") ? "text-primary-500" : "text-foreground-700"}`}>Buy Data</Link>
          <Link to="/reseller" onClick={() => setMobileMenuOpen(false)} className={`block py-2 text-sm font-medium ${isActive("/reseller") ? "text-secondary-600" : "text-foreground-700"}`}>Become a Reseller</Link>
          <Link to="/monthly-challenge" onClick={() => setMobileMenuOpen(false)} className={`block py-2 text-sm font-medium ${isActive("/monthly-challenge") ? "text-accent-600" : "text-foreground-700"}`}>What&apos;s New</Link>
          <Link to="/track-order" onClick={() => setMobileMenuOpen(false)} className={`block py-2 text-sm font-medium ${isActive("/track-order") ? "text-primary-500" : "text-foreground-700"}`}>Track Order</Link>
          <div className="border-t border-foreground-100 pt-1 mt-1"></div>
          <Link to="/login" onClick={() => setMobileMenuOpen(false)} className="block py-2 text-sm font-medium text-foreground-700">Sign In</Link>
          <Link to="/register" onClick={() => setMobileMenuOpen(false)} className="block py-2 text-sm font-medium text-foreground-700">Create Account</Link>
        </div>
      )}
    </nav>
  );
}